import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from '../../utils/getApiUrl';
import { useSelectedDictionary } from '../../context/selectedContext';
import type { DictionaryRowModel } from '../../type/dictionary';
import type { WordModel } from "../../type/word";
import type { UserParamsMap, UserModel } from "../../type/user";
import { fetchWithAuth } from "../../auth/auth";
import { cancelIcon, okIcon } from "../../utils/config";
import ConfirmDialog from "../../modal/confirmDialog/confirmDialog";
import "./dictionaryRow.scss";
import EditButton from "../../components/editButton/editButton";
import DeleteButton from "../../components/deleteButton/deleteButton";

const DictionaryRow: React.FC<
  DictionaryRowModel & 
  { showToast: (message: string, type?: 'info' | 'warning' | 'error') => void } &
  { parameters: UserParamsMap | undefined } &
  { index: number } &
  { user: UserModel | null }
  > = ({ dict, onUpdate, onDelete, showToast, parameters, index, user }) => {
  
  const navigate = useNavigate();
  const { setSelected } = useSelectedDictionary();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(dict.name);
  const [saving, setSaving] = useState(false);
  const [wordCount, setWordCount] = useState<number | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    const fetchWordCount = async () => {
      try {
        const res = await fetchWithAuth(getApiUrl(`words?iddictionary=${dict.iddictionary}`));
        if (!res.ok) return;
        const data: WordModel[] = await res.json();
        const count = Array.isArray(data) ? data.filter((w: WordModel) => !w.deleted).length : 0;
        setWordCount(count);
      } catch (err) {
        console.error("Failed to fetch word count:", err);
      }
    };

    fetchWordCount();
  }, [dict.iddictionary]);

  const handleDeleteClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmDialog(false);
    await onDelete(dict.iddictionary);
  };  

  const openDict = () => {
    if (isEditing) return;
    setSelected({ id: dict.iddictionary, name: dict.name });
    navigate(`/words?iddictionary=${encodeURIComponent(String(dict.iddictionary))}`);
  };

  const save = async () => {
    if (!name.trim()) return showToast("Please enter a name.", "warning");
    setSaving(true);
    try {
      await onUpdate(dict.iddictionary, name.trim());
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`dictionaries-item${showConfirmDialog ? " dictionaries-item--elevated" : ""}`}
      role="listitem"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="dictionaries-item__icon" onClick={openDict}>
        <i className="bi bi-book" />
      </div>

      <div className="dictionaries-item__content" onClick={openDict}>
        {!isEditing ? (
          <>
            <span className="dictionaries-item__name" title={dict.name}>
              {dict.name}
            </span>
              <span 
                className={`dictionaries-item__count ${
                  wordCount !== null && user?.max_expressions !== -1 && wordCount >= (user?.max_expressions ?? 0)
                    ? 'dictionaries-item__red'
                    : ''
                }`}
              >
              {wordCount !== null
                ? `${wordCount} ${wordCount === 1 ? "expression" : "expressions"}`
                : "..."
              }
            </span>
          </>
        ) : (
          <input
            className="form-control dictionaries-item__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") {
                setName(dict.name);
                setIsEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            maxLength={30}
          />
        )}
      </div>

      <div className="dictionaries-item__actions">
        {isEditing ? (
          <>
            <button
              className="dictionaries-item__action-btn dictionaries-item__action-btn--save"
              onClick={save}
              disabled={saving}
              title="Save"
            >
              {okIcon}
            </button>
            <button
              className="dictionaries-item__action-btn dictionaries-item__action-btn--cancel"
              onClick={() => {
                setName(dict.name);
                setIsEditing(false);
              }}
              title="Cancel"
            >
              {cancelIcon}
            </button>
          </>
        ) : (
          <>
            {parameters?.editbuttons_visible.value && (
              <>
                <EditButton
                  className="dictionaries-item__action-btn dictionaries-item__action-btn--edit"
                  onClick={() => setIsEditing(true)}
                />
                <DeleteButton
                  className="dictionaries-item__action-btn dictionaries-item__action-btn--delete"
                  onClick={handleDeleteClick}
                />
              </>
            )}
            <div className="dictionaries-item__arrow" onClick={openDict}>
              <i className="bi bi-chevron-right" />
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        message="You sure you wanna delete this dictionary?"
        title="Confirm Deletion"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowConfirmDialog(false)}
      />      
    </div>
  );
};

export default DictionaryRow;